import { useState, lazy, Suspense } from "react";
import { looksLikeArticle, type AnchoredQuote, type HighlightedComment } from "./ArticleRender";
import { MarkdownText, hasMarkdown } from "./MarkdownText";

// TipTap is heavy (~250KB + per-instance editor mount cost). Only load it when
// a caller actually opts into selection-to-comment by passing onQuoteSelected
// or highlights. Read-only views (upstream outputs, drawer body, key-value
// rows) get the lightweight MarkdownText path instead.
const ArticleRender = lazy(() =>
  import("./ArticleRender").then((m) => ({ default: m.ArticleRender })),
);

/**
 * Renders a step's output for business users. Agents emit varied shapes —
 * usually `{ summary, rows[] }`, sometimes `{ summary }`, an array of rows,
 * or a plain string. We detect each and render the human-friendly view;
 * raw JSON is available behind a "Show raw" toggle as a last resort.
 */
export function OutputRender({
  output,
  onQuoteSelected,
  highlights,
}: {
  output: unknown;
  onQuoteSelected?: ((q: AnchoredQuote) => void) | undefined;
  highlights?: HighlightedComment[] | undefined;
}) {
  if (output == null || output === "") {
    return null;
  }
  const wantsInteractive = !!onQuoteSelected || !!highlights;
  if (wantsInteractive && looksLikeArticle(output)) {
    return (
      <Suspense fallback={<MarkdownText className="or-text">{output}</MarkdownText>}>
        <ArticleRender
          content={output}
          {...(onQuoteSelected && { onQuoteSelected })}
          {...(highlights && { highlights })}
        />
      </Suspense>
    );
  }
  if (typeof output === "string") {
    if (hasMarkdown(output)) return <MarkdownText className="or-text">{output}</MarkdownText>;
    return <p className="or-text">{output}</p>;
  }
  if (typeof output === "number" || typeof output === "boolean") {
    return <p className="or-text">{String(output)}</p>;
  }
  if (Array.isArray(output)) {
    if (output.length === 0) return null;
    if (isRowArray(output)) return <Table rows={output} />;
    return <RawJson value={output} />;
  }
  if (typeof output === "object") {
    return <ObjectRender obj={output as Record<string, unknown>} />;
  }
  return null;
}

function ObjectRender({ obj }: { obj: Record<string, unknown> }) {
  const summary = typeof obj["summary"] === "string" ? (obj["summary"] as string) : null;
  const rows = Array.isArray(obj["rows"]) && isRowArray(obj["rows"]) ? (obj["rows"] as Row[]) : null;
  const drivers = Array.isArray(obj["drivers"]) && obj["drivers"].every((d) => typeof d === "string")
    ? (obj["drivers"] as string[])
    : null;
  const options = Array.isArray(obj["options"]) ? (obj["options"] as unknown[]) : null;

  const knownKeys = new Set(["summary", "rows", "drivers", "options"]);
  const otherEntries = Object.entries(obj).filter(([k, v]) => !knownKeys.has(k) && v != null && v !== "");

  return (
    <div className="or-stack">
      {summary && (hasMarkdown(summary)
        ? <MarkdownText className="or-text">{summary}</MarkdownText>
        : <p className="or-text">{summary}</p>)}
      {rows && <Table rows={rows} />}
      {drivers && (
        <ul className="or-list">
          {drivers.map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      )}
      {options && (
        <ul className="or-list">
          {options.map((o, i) => <li key={i}>{typeof o === "string" ? o : JSON.stringify(o)}</li>)}
        </ul>
      )}
      {otherEntries.length > 0 && <KeyValueList entries={otherEntries} />}
    </div>
  );
}

type Row = Record<string, unknown>;

function isRowArray(arr: unknown[]): arr is Row[] {
  return arr.every(
    (x) =>
      x != null &&
      typeof x === "object" &&
      !Array.isArray(x) &&
      Object.values(x as object).every(
        (v) =>
          v == null ||
          typeof v === "string" ||
          typeof v === "number" ||
          typeof v === "boolean",
      ),
  );
}

function Table({ rows }: { rows: Row[] }) {
  const cols = Array.from(
    rows.reduce<Set<string>>((acc, r) => {
      Object.keys(r).forEach((k) => acc.add(k));
      return acc;
    }, new Set()),
  );
  return (
    <div className="or-table-wrap">
      <table className="or-table">
        <thead>
          <tr>
            {cols.map((c) => <th key={c}>{prettifyKey(c)}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {cols.map((c) => <td key={c}>{formatCell(r[c], c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KeyValueList({ entries }: { entries: Array<[string, unknown]> }) {
  return (
    <dl className="or-dl">
      {entries.map(([k, v]) => (
        <div key={k} className="or-dl-row">
          <dt>{prettifyKey(k)}</dt>
          <dd>
            {typeof v === "string"
              ? (hasMarkdown(v) ? <MarkdownText>{v}</MarkdownText> : v)
              : typeof v === "number" || typeof v === "boolean"
                ? String(v)
                : <RawJson value={v} compact />}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function formatCell(v: unknown, col: string): React.ReactNode {
  if (v == null) return <span className="or-muted">—</span>;
  if (typeof v === "number") {
    // Heuristic: column names ending in _pct, _percent → render with % sign.
    if (/(_pct|_percent|_ratio)$/i.test(col)) {
      const sign = v > 0 ? "+" : "";
      return <span className={v < 0 ? "or-neg" : v > 0 ? "or-pos" : ""}>{sign}{v}%</span>;
    }
    return String(v);
  }
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

function prettifyKey(k: string): string {
  return k
    .replace(/_/g, " ")
    .replace(/\bpct\b/gi, "%")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function RawJson({ value, compact }: { value: unknown; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const text = JSON.stringify(value, null, 2);
  return (
    <div className="or-raw">
      <button
        type="button"
        className="or-raw-toggle"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? "Hide raw data" : compact ? "Show details" : "Show raw data"}
      </button>
      {open && <pre className="or-pre">{text}</pre>}
    </div>
  );
}
